import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getLandingCardColors } from '@/data/recorridos/config';
import { getHorarioVueltas, getSalidas } from '@/data/recorridos/horarios';
import { motivoSinServicioHoy } from '@/lib/calendarioServicio';

const HORA_SANTIAGO = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'America/Santiago',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const aMinutos = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** Minutos desde medianoche en hora de Chile (el usuario puede estar en otra zona horaria). */
function useMinutosSantiago() {
  const [minutos, setMinutos] = useState(() => aMinutos(HORA_SANTIAGO.format(new Date())));
  useEffect(() => {
    const id = setInterval(() => setMinutos(aMinutos(HORA_SANTIAGO.format(new Date()))), 30000);
    return () => clearInterval(id);
  }, []);
  return minutos;
}

const SENTIDOS = [
  { id: 'ida', label: 'Ida' },
  { id: 'vuelta', label: 'Regreso' },
];

const labelSentido = (s) => (s === 'ida' ? 'Ida' : 'Regreso');

function SalidasSentido({ recorridoId, sentido, label, color, ahora, sinServicioHoy }) {
  const { origen, destino, salidas } = useMemo(
    () => getSalidas(recorridoId, sentido),
    [recorridoId, sentido]
  );
  if (salidas.length === 0) return null;

  /** Sin próxima salida hoy (fin de servicio o fin de semana): horario completo sin atenuar. */
  const idxProxima = sinServicioHoy ? -1 : salidas.findIndex((s) => aMinutos(s.hora) >= ahora);

  return (
    <div data-testid={`horarios-${sentido}`}>
      <p className="text-[11px] font-extrabold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-semibold leading-snug text-foreground">
        Desde {origen}
        {destino && <span className="font-medium text-muted-foreground"> hacia {destino}</span>}
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {salidas.map((s, i) => {
          const esProxima = i === idxProxima;
          const pasada = idxProxima !== -1 && i < idxProxima;
          return (
            <li
              key={`${s.hora}-${i}`}
              className={cn(
                'flex min-w-[3.75rem] max-w-[9rem] flex-col items-center rounded-xl border px-2.5 py-1.5 text-center',
                esProxima
                  ? 'border-transparent text-white shadow-md'
                  : 'border-border/60 bg-card text-foreground',
                pasada && 'bg-muted/40 text-muted-foreground'
              )}
              style={esProxima ? { backgroundColor: color } : undefined}
            >
              {esProxima && (
                <span className="text-[9px] font-extrabold uppercase tracking-widest">Próxima</span>
              )}
              <span className="font-headline text-base font-bold tabular-nums leading-tight">
                {s.hora}
              </span>
              {s.detalle && (
                <span
                  className={cn(
                    'text-[10px] font-semibold leading-tight',
                    esProxima ? 'text-white/90' : 'text-muted-foreground'
                  )}
                >
                  {s.detalle}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TramoDetalle({ tramo, color }) {
  return (
    <div>
      <p className="flex flex-wrap items-center gap-x-2 text-[11px] font-extrabold uppercase tracking-widest">
        <span style={{ color }}>{labelSentido(tramo.sentido)}</span>
        {tramo.enTransito && <span className="text-muted-foreground">En tránsito</span>}
        {tramo.variante && <span className="text-muted-foreground">{tramo.variante}</span>}
      </p>
      <ol className="mt-1 divide-y divide-border/50">
        {tramo.puntos.map(([nombre, hora], i) => (
          <li
            key={`${nombre}-${i}`}
            className={cn(
              'flex items-baseline justify-between gap-3 py-1 text-sm',
              tramo.enTransito ? 'text-muted-foreground' : 'text-foreground'
            )}
          >
            <span className={cn('leading-snug', i === 0 && 'font-semibold')}>{nombre}</span>
            <span className={cn('shrink-0 tabular-nums', i === 0 ? 'font-bold' : 'font-medium')}>
              {hora}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Horarios fijos de salida del recorrido (itinerario oficial): salidas de ida y de regreso,
 * con detalle opcional por punto de control.
 * @param {{ recorrido: { id: string; codigo?: string }; detalleAbierto?: boolean }} props
 */
export default function HorariosSalida({ recorrido, detalleAbierto = false }) {
  const [verDetalle, setVerDetalle] = useState(detalleAbierto);
  const ahora = useMinutosSantiago();
  const motivoSinServicio = motivoSinServicioHoy();
  const sinServicioHoy = motivoSinServicio != null;
  const vueltas = useMemo(() => getHorarioVueltas(recorrido?.id), [recorrido?.id]);

  if (vueltas.length === 0) return null;

  const color = getLandingCardColors(recorrido).background;
  const hayTransito = vueltas.some((v) => v.tramos.some((t) => t.enTransito));

  return (
    <section
      className="rounded-2xl border border-border/50 bg-card p-4 shadow-card"
      aria-label={`Horarios de salida ${recorrido.codigo || ''}`}
      data-testid="horarios-salida"
    >
      <div className="mb-3 flex items-center gap-2">
        <Clock className="h-5 w-5 shrink-0" style={{ color }} aria-hidden />
        <h3 className="font-headline text-lg font-bold text-foreground">Horarios de salida</h3>
        {recorrido.codigo && (
          <span
            className="ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-bold text-white"
            style={{ backgroundColor: color }}
          >
            {recorrido.codigo}
          </span>
        )}
      </div>

      <div className="space-y-4">
        {SENTIDOS.map((s) => (
          <SalidasSentido
            key={s.id}
            recorridoId={recorrido.id}
            sentido={s.id}
            label={s.label}
            color={color}
            ahora={ahora}
            sinServicioHoy={sinServicioHoy}
          />
        ))}
      </div>

      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        Horario fijo del itinerario oficial, de lunes a viernes salvo feriados
        {sinServicioHoy ? ` (hoy no hay servicio: ${motivoSinServicio})` : ''}. Puede variar por
        tránsito o contingencias.
      </p>

      <button
        type="button"
        onClick={() => setVerDetalle((v) => !v)}
        className="mt-3 flex w-full items-center justify-between rounded-xl border border-border/60 px-3 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-muted/40"
        aria-expanded={verDetalle}
        data-testid="horarios-detalle-toggle"
      >
        {verDetalle ? 'Ocultar horas por punto de control' : 'Ver horas por punto de control'}
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', verDetalle && 'rotate-180')}
          aria-hidden
        />
      </button>

      {verDetalle && (
        <div className="mt-4 space-y-5" data-testid="horarios-detalle">
          {vueltas.map((v) => (
            <div key={v.vuelta}>
              <p className="mb-2 rounded-lg bg-muted/50 px-3 py-1.5 text-xs font-extrabold uppercase tracking-widest text-foreground">
                Vuelta {v.vuelta}
              </p>
              <div className="space-y-3 px-1">
                {v.tramos.map((t, i) => (
                  <TramoDetalle key={`${t.sentido}-${i}`} tramo={t} color={color} />
                ))}
                {v.notas?.map((n) => (
                  <p key={n} className="text-xs leading-relaxed text-muted-foreground">
                    {n}
                  </p>
                ))}
              </div>
            </div>
          ))}
          {hayTransito && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              «En tránsito»: traslado del bus entre cabeceras por Acceso Sur, fuera del recorrido
              habitual.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
